import { useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useNavigate } from 'react-router-dom'
import api from '@/services/api'
import { authService } from '@/services/auth'
import { useAuthStore } from '@/stores/authStore'
import { useRecaptchaSiteKey } from '@/hooks/useRecaptchaSiteKey'
import { DateOfBirthInput } from '@/components/ui/DateOfBirthInput'
import { PostcodeLookup } from '@/components/ui/PostcodeLookup'
import { Address } from '@/services/postcode'
import { getStoredReferralCode } from '@/utils/referralTracking'
import { setAuthReturn } from '@/utils/authReturn'

/**
 * Sign up or sign in WITHOUT leaving checkout (Sales Strategy, owner 2026-10-15).
 *
 * Before: register (2 pages) → text code → email code → sign in again → another text code →
 * back to checkout. 147 of 194 people who left the basket in 60 days never got signed in.
 * Now: one form here, one text code, and they carry on to payment with their basket on screen.
 * Same fields, same checks and the same reCAPTCHA as the Register page; the email address is
 * confirmed afterwards by the usual "complete verification" prompt.
 */
const BOX = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'
const pwOk = (p: string) => p.length >= 8 && /\d/.test(p) && /[!@#$%^&*(),.?":{}|<>]/.test(p)
const errText = (e: any) => {
  const d = e?.response?.data?.detail
  if (Array.isArray(d)) return d.map((x: any) => x.msg).join(', ')
  return d || e?.response?.data?.message || 'Something went wrong — please try again.'
}

export default function CheckoutAccount() {
  const [tab, setTab] = useState<'new' | 'existing'>('new')
  const siteKey = useRecaptchaSiteKey()
  const navigate = useNavigate()
  const storeLogin = useAuthStore((s) => s.login)
  const recRef = useRef<ReCAPTCHA>(null)
  const [rec, setRec] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // new customer
  const [f, setF] = useState({
    title: '', first_name: '', last_name: '', gender: '', date_of_birth: '', email: '', mobile_number: '',
    address_line1: '', address_line2: '', city: '', county: '', postcode: '', password: '', confirm: '',
  })
  const [manualAddr, setManualAddr] = useState(false)
  const [waWarning, setWaWarning] = useState<string | null>(null)
  const [waOk, setWaOk] = useState(false)
  const [codeStep, setCodeStep] = useState(false)
  const [code, setCode] = useState('')
  const [resent, setResent] = useState(false)

  // existing customer
  const [login, setLogin] = useState({ email: '', password: '' })
  const [otpToken, setOtpToken] = useState<string | null>(null)
  const [otpHint, setOtpHint] = useState('')
  const [otp, setOtp] = useState('')

  const resetRec = () => { recRef.current?.reset(); setRec(null) }
  const set = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }))
  const pickAddress = (a: Address) => setF((x) => ({
    ...x, address_line1: a.line_1, address_line2: [a.line_2, a.line_3].filter(Boolean).join(', '),
    city: a.post_town, county: a.county || '', postcode: a.postcode,
  }))

  const finish = (res: any) => {
    storeLogin(res.access_token, res.refresh_token, res.user)
    if (res.must_complete_profile) { setAuthReturn('/checkout'); navigate('/complete-profile') }
    // otherwise the checkout re-renders with the customer signed in — basket untouched
  }

  const createAccount = async () => {
    setError(null)
    const missing = (['title', 'first_name', 'last_name', 'gender', 'date_of_birth', 'email', 'mobile_number', 'address_line1', 'city', 'postcode'] as const)
      .filter((k) => !(f as any)[k]?.trim())
    if (missing.length) return setError('Please fill in every field marked *, including your address.')
    if (!pwOk(f.password)) return setError('Your password needs at least 8 characters, a number and a symbol (e.g. ! or ?).')
    if (f.password !== f.confirm) return setError("The two passwords don't match.")
    if (siteKey && !rec) return setError('Please tick "I\'m not a robot" first.')
    if (!waOk) {
      try {
        const { data } = await api.post('/auth/otp-channel-check', { mobile_number: f.mobile_number })
        if (data?.requires_confirmation) { setWaWarning(data.warning); return }
      } catch { /* the server checks again — never block on this */ }
    }
    setBusy(true)
    try {
      await authService.register({
        title: f.title, first_name: f.first_name.trim(), last_name: f.last_name.trim(), gender: f.gender,
        date_of_birth: f.date_of_birth, email: f.email.trim(), mobile_number: f.mobile_number.trim(),
        address_line1: f.address_line1, address_line2: f.address_line2 || undefined, city: f.city,
        county: f.county || undefined, postcode: f.postcode, country: 'United Kingdom', password: f.password,
        password_confirm: f.confirm, recaptcha_token: rec ?? '', referral_code: getStoredReferralCode() || undefined,
      } as any)
      setCodeStep(true)
    } catch (e) { setError(errText(e)); resetRec() }
    finally { setBusy(false) }
  }

  const verifyCode = async () => {
    setError(null)
    if (!/^\d{6}$/.test(code.trim())) return setError('Enter the 6-digit code we sent you.')
    setBusy(true)
    try {
      const { data } = await api.post('/auth/checkout-signup-verify', { email: f.email.trim(), password: f.password, code: code.trim() })
      finish(data)
    } catch (e) { setError(errText(e)) }
    finally { setBusy(false) }
  }

  const resend = async () => {
    setError(null)
    try { await authService.resendVerification(f.email.trim(), 'mobile'); setResent(true) } catch (e) { setError(errText(e)) }
  }

  const signIn = async () => {
    setError(null)
    if (!login.email || !login.password) return setError('Enter your email and password.')
    if (siteKey && !rec) return setError('Please tick "I\'m not a robot" first.')
    setBusy(true)
    try {
      const res: any = await authService.login({ email: login.email.trim(), password: login.password, recaptcha_token: rec || undefined } as any)
      resetRec()
      if (res.access_token) return finish(res)
      setOtpToken(res.otp_token); setOtpHint(res.message || 'We sent you a code.')
    } catch (e) { setError(errText(e)); resetRec() }
    finally { setBusy(false) }
  }

  const verifyOtp = async () => {
    setError(null)
    setBusy(true)
    try {
      const res: any = await authService.verifyLoginOTP({ otp_token: otpToken!, otp_code: otp.trim() })
      if (res.access_token) return finish(res)
      setError(res.message || 'That code is not right.')
    } catch (e) { setError(errText(e)) }
    finally { setBusy(false) }
  }

  const recaptcha = siteKey ? (
    <div className="my-3"><ReCAPTCHA ref={recRef} sitekey={siteKey} onChange={(t) => setRec(t)} onExpired={() => setRec(null)} /></div>
  ) : null

  return (
    <div className="max-w-xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-bold mb-1">Almost there</h1>
      <p className="text-gray-600 mb-6">Your basket is saved. Tell us who you are and you'll go straight on to payment.</p>
      <div className="flex rounded-lg border overflow-hidden mb-6">
        {(['new', 'existing'] as const).map((t) => (
          <button key={t} onClick={() => { setTab(t); setError(null) }}
            className={`flex-1 py-2.5 text-sm font-semibold ${tab === t ? 'bg-primary-600 text-white' : 'bg-white text-gray-700'}`}>
            {t === 'new' ? "I'm new here" : 'I have an account'}
          </button>
        ))}
      </div>

      {tab === 'new' && !codeStep && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select className={BOX} value={f.title} onChange={(e) => set('title', e.target.value)}>
              <option value="">Title *</option>{['Mr', 'Mrs', 'Miss', 'Ms', 'Dr'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className={BOX} value={f.gender} onChange={(e) => set('gender', e.target.value)}>
              <option value="">Gender *</option><option value="male">Male</option><option value="female">Female</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
            <input className={BOX} placeholder="First name *" autoComplete="given-name" value={f.first_name} onChange={(e) => set('first_name', e.target.value)} />
            <input className={BOX} placeholder="Last name *" autoComplete="family-name" value={f.last_name} onChange={(e) => set('last_name', e.target.value)} />
          </div>
          <div><label className="block text-sm text-gray-700 mb-1">Date of birth *</label>
            <DateOfBirthInput value={f.date_of_birth} onChange={(v) => set('date_of_birth', v)} /></div>
          <input className={BOX} type="email" placeholder="Email *" autoComplete="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
          <input className={BOX} type="tel" placeholder="Mobile number * (we'll text you a code)" autoComplete="tel" value={f.mobile_number} onChange={(e) => { set('mobile_number', e.target.value); setWaOk(false); setWaWarning(null) }} />
          {waWarning && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
              <p>{waWarning}</p>
              <button className="mt-2 px-3 py-1.5 bg-amber-600 text-white rounded" onClick={() => { setWaOk(true); setWaWarning(null) }}>Yes, I have WhatsApp on this number</button>
            </div>
          )}
          {!manualAddr ? (
            <div>
              <PostcodeLookup onAddressSelect={pickAddress} initialPostcode={f.postcode} label="Your address — postcode *" />
              {f.address_line1 && <p className="text-sm text-gray-700 mt-1">✓ {[f.address_line1, f.address_line2, f.city, f.postcode].filter(Boolean).join(', ')}</p>}
              <button type="button" className="text-sm text-primary-600 mt-1" onClick={() => setManualAddr(true)}>Enter address manually</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <input className={`${BOX} col-span-2`} placeholder="Address line 1 *" value={f.address_line1} onChange={(e) => set('address_line1', e.target.value)} />
              <input className={`${BOX} col-span-2`} placeholder="Address line 2" value={f.address_line2} onChange={(e) => set('address_line2', e.target.value)} />
              <input className={BOX} placeholder="Town / city *" value={f.city} onChange={(e) => set('city', e.target.value)} />
              <input className={BOX} placeholder="Postcode *" value={f.postcode} onChange={(e) => set('postcode', e.target.value.toUpperCase())} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <input className={BOX} type="password" placeholder="Choose a password *" autoComplete="new-password" value={f.password} onChange={(e) => set('password', e.target.value)} />
            <input className={BOX} type="password" placeholder="Repeat password *" autoComplete="new-password" value={f.confirm} onChange={(e) => set('confirm', e.target.value)} />
          </div>
          <p className="text-xs text-gray-500">At least 8 characters, with a number and a symbol.</p>
          {recaptcha}
          <button disabled={busy} onClick={createAccount} className="w-full py-3 bg-primary-600 text-white rounded-lg font-semibold disabled:opacity-50">
            {busy ? 'Creating your account…' : 'Continue'}
          </button>
        </div>
      )}

      {tab === 'new' && codeStep && (
        <div className="space-y-3">
          <p className="text-gray-700">We've sent a 6-digit code to <b>{f.mobile_number}</b>. Enter it to carry on to payment.</p>
          <input className={`${BOX} text-center text-2xl tracking-widest`} inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
          <button disabled={busy} onClick={verifyCode} className="w-full py-3 bg-primary-600 text-white rounded-lg font-semibold disabled:opacity-50">{busy ? 'Checking…' : 'Confirm and continue'}</button>
          <p className="text-sm text-gray-600">No code? {resent ? 'Sent again — it can take a minute.' : <button className="text-primary-600" onClick={resend}>Send it again</button>}</p>
        </div>
      )}

      {tab === 'existing' && !otpToken && (
        <div className="space-y-3">
          <input className={BOX} type="email" placeholder="Email" autoComplete="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
          <input className={BOX} type="password" placeholder="Password" autoComplete="current-password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
          {recaptcha}
          <button disabled={busy} onClick={signIn} className="w-full py-3 bg-primary-600 text-white rounded-lg font-semibold disabled:opacity-50">{busy ? 'Signing in…' : 'Sign in and continue'}</button>
          <p className="text-sm text-gray-600"><button className="text-primary-600" onClick={() => { setAuthReturn('/checkout'); navigate('/forgot-password') }}>Forgotten your password?</button>
            {' · '}<button className="text-primary-600" onClick={() => { setAuthReturn('/checkout'); navigate('/first-time-login') }}>Customer but never signed in?</button></p>
        </div>
      )}

      {tab === 'existing' && otpToken && (
        <div className="space-y-3">
          <p className="text-gray-700">{otpHint}</p>
          <input className={`${BOX} text-center text-2xl tracking-widest`} inputMode="numeric" maxLength={8} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} autoFocus />
          <button disabled={busy} onClick={verifyOtp} className="w-full py-3 bg-primary-600 text-white rounded-lg font-semibold disabled:opacity-50">{busy ? 'Checking…' : 'Confirm and continue'}</button>
        </div>
      )}

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
    </div>
  )
}
